import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { LEARN } from "../theme";
import LearnImage from "../media/LearnImage";
import LearnCompanion from "../LearnCompanion";
import { useBreakpoint } from "./LearnShell";

/**
 * The game surface. Full-screen takeover (navbar hidden by App.js route
 * policy) that composes the SAME game engines into a real desktop layout:
 *
 *   ┌──────────────────────────────────────────────────────┐
 *   │ ✕  Title                              ●●●○○  progress │
 *   │ ┌──────────┐ ┌──────────────────────┐ ┌────────────┐ │
 *   │ │ prompt   │ │                      │ │ companion  │ │
 *   │ │ bubble   │ │      ARENA           │ │ reactions  │ │
 *   │ │ 🔊 hint  │ │  (engine renders)    │ │ tips       │ │
 *   │ └──────────┘ └──────────────────────┘ └────────────┘ │
 *   │                     [   Next   ]                     │
 *   └──────────────────────────────────────────────────────┘
 *
 * On phones: rails collapse into a top strip and a bottom bar; the arena is
 * full-bleed. The arena exposes its size through CSS custom properties
 * (--arena-w/--arena-h/--tile) so engines size tiles to the space they get.
 */
export default function LearnStage({
  title,
  prompt,
  background,      // asset key
  accent = LEARN.color.blue,
  accentDeep = LEARN.color.blueDeep,
  progress,        // { done, total }
  companionPose,   // idle | think | cheer | oops
  hint,
  onClose,
  onReplayPrompt,
  actionLabel = "Next",
  actionEnabled = false,
  onAction,
  speaking = false,
  tone = "image",  // image: art backdrop, white chrome · light: soft wash, ink chrome (writing pads)
  subtitle,
  children,
}) {
  const bp = useBreakpoint();
  const compact = bp === "xs" || bp === "sm";
  const mid = bp === "md"; // laptops/tablets: drop the companion column so the arena keeps its width
  const arenaRef = useRef(null);
  // Estimate before the first measurement so engines that read --tile on mount
  // don't see the 56px floor and size their pieces for a phone.
  const [arena, setArena] = useState(() => {
    if (typeof window === "undefined") return { w: 760, h: 620 };
    const small = window.innerWidth < LEARN.bp.md;
    return small
      ? { w: window.innerWidth - 24, h: Math.max(320, window.innerHeight - 260) }
      : { w: Math.round(window.innerWidth * 0.56), h: Math.max(400, window.innerHeight - 186) };
  });

  useLayoutEffect(() => {
    const el = arenaRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(([e]) => {
      const { width, height } = e.contentRect;
      setArena({ w: Math.round(width), h: Math.round(height) });
    });
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = prev; };
  }, []);

  const tile = Math.max(56, Math.min(112, Math.floor(Math.min(arena.w / 6.5, arena.h / 6))));

  const Prompt = prompt ? (
    <div
      style={{
        display: "flex", alignItems: "center", gap: 10, background: "rgba(255,255,255,0.94)",
        borderRadius: LEARN.radius.lg, padding: compact ? "10px 14px" : "16px 18px",
        boxShadow: LEARN.shadow.card, border: `2px solid ${LEARN.color.line}`,
      }}
    >
      <p style={{ margin: 0, fontSize: compact ? 15 : 18, fontWeight: 600, lineHeight: 1.3, color: LEARN.color.ink, flex: 1 }}>{prompt}</p>
      {onReplayPrompt && (
        <button
          type="button"
          aria-label="Hear it again"
          onClick={onReplayPrompt}
          style={{
            flexShrink: 0, width: 44, height: 44, borderRadius: "50%", border: "none", cursor: "pointer",
            background: accent, color: "#fff", boxShadow: LEARN.shadow.sticker(accentDeep),
            display: "grid", placeItems: "center",
          }}
        >
          <SpeakerIcon animate={speaking} />
        </button>
      )}
    </div>
  ) : null;

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 9000, fontFamily: LEARN.font, color: LEARN.color.ink,
        background: "#BEE3FF", overflow: "hidden", userSelect: "none",
        "--arena-w": `${arena.w}px`, "--arena-h": `${arena.h}px`, "--tile": `${tile}px`,
      }}
    >
      {background && tone === "image" && <LearnImage asset={background} fit="cover" eager style={{ position: "absolute", inset: 0 }} />}
      {background && tone === "light" && (
        // soft, out-of-focus scenery so the paper doesn't float on a flat gradient
        <LearnImage asset={background} fit="cover" eager style={{ position: "absolute", inset: -40, opacity: 0.55, filter: "blur(22px) saturate(0.9)" }} />
      )}
      <div style={{ position: "absolute", inset: 0, pointerEvents: "none", background: tone === "light"
        ? `linear-gradient(180deg, rgba(255,255,255,0.92) 0%, ${accent}14 45%, rgba(246,247,251,0.9) 100%)`
        : "linear-gradient(180deg, rgba(11,26,51,0.30) 0%, rgba(11,26,51,0.06) 30%, rgba(11,26,51,0.06) 70%, rgba(11,26,51,0.34) 100%)" }} />

      {/* Top bar */}
      <div style={{ position: "absolute", left: 0, right: 0, top: 0, padding: "max(14px, env(safe-area-inset-top)) 16px 0", display: "flex", alignItems: "center", gap: 12, zIndex: 5 }}>
        <motion.button
          type="button"
          aria-label="Close"
          onClick={onClose}
          whileTap={{ scale: 0.92 }}
          style={{ width: 48, height: 48, borderRadius: "50%", border: `2px solid #fff`, background: "rgba(255,255,255,0.95)", boxShadow: LEARN.shadow.card, cursor: "pointer", display: "grid", placeItems: "center", flexShrink: 0 }}
        >
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={LEARN.color.ink} strokeWidth="3" strokeLinecap="round"><path d="M6 6l12 12M18 6L6 18" /></svg>
        </motion.button>
        {title && (
          <div style={{ flex: 1, minWidth: 0 }}>
            <h1 style={{ margin: 0, fontSize: compact ? 20 : 26, fontWeight: 700, color: tone === "light" ? LEARN.color.ink : "#fff", textShadow: tone === "light" ? "none" : "0 2px 10px rgba(11,26,51,0.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{title}</h1>
            {subtitle && <div style={{ fontSize: 13, fontWeight: 600, color: tone === "light" ? LEARN.color.muted : "rgba(255,255,255,0.9)", textShadow: tone === "light" ? "none" : "0 1px 6px rgba(11,26,51,0.5)", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{subtitle}</div>}
          </div>
        )}
        {progress && progress.total > 0 && (
          <div aria-label={`${progress.done} of ${progress.total}`} style={{ display: "flex", gap: 6, alignItems: "center", background: "rgba(255,255,255,0.92)", padding: "8px 12px", borderRadius: 999, boxShadow: LEARN.shadow.card }}>
            {Array.from({ length: progress.total }, (_, i) => (
              <span key={i} style={{ width: 10, height: 10, borderRadius: 999, background: i < progress.done ? accent : "rgba(11,26,51,0.14)", transition: "background 200ms" }} />
            ))}
          </div>
        )}
      </div>

      {/* Body grid */}
      <div
        style={{
          position: "absolute", inset: 0,
          paddingTop: compact ? 74 : 86,
          paddingBottom: compact ? 92 : 100,
          paddingLeft: compact ? 12 : 24,
          paddingRight: compact ? 12 : 24,
          display: "grid",
          gridTemplateColumns: compact ? "1fr" : mid ? "minmax(200px, 250px) minmax(0, 1fr)" : "minmax(220px, 300px) minmax(0, 1fr) minmax(160px, 220px)",
          gridTemplateRows: compact ? "auto minmax(0, 1fr)" : "minmax(0, 1fr)",
          gap: compact ? 10 : 20,
          alignItems: "stretch",
        }}
      >
        {/* Left rail / mobile top strip */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, justifyContent: compact ? "flex-start" : "center", minWidth: 0 }}>
          {Prompt}
          {!compact && hint && (
            <div style={{ background: "rgba(255,255,255,0.7)", borderRadius: LEARN.radius.md, padding: "10px 14px", fontSize: 14, color: LEARN.color.muted }}>{hint}</div>
          )}
        </div>

        {/* Arena */}
        <div
          ref={arenaRef}
          className="learn-arena"
          style={{ position: "relative", minWidth: 0, minHeight: 0, display: "flex", flexDirection: "column" }}
        >
          {children}
        </div>

        {/* Right rail: companion */}
        {!compact && !mid && (
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", gap: 10, minWidth: 0 }}>
            <LearnCompanion size={190} pose={companionPose} mood={tone === "light" ? "writing" : "default"} speaking={speaking} />
          </div>
        )}
      </div>

      {/* Bottom action */}
      <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, padding: "0 16px max(16px, env(safe-area-inset-bottom))", display: "flex", justifyContent: "center", alignItems: "flex-end", gap: 12, zIndex: 5, pointerEvents: "none" }}>
        {compact && <div style={{ pointerEvents: "none", position: "absolute", left: 10, bottom: 8 }}><LearnCompanion size={80} pose={companionPose} mood={tone === "light" ? "writing" : "default"} speaking={speaking} /></div>}
        {mid && <div style={{ pointerEvents: "none", position: "absolute", right: 14, bottom: 6 }}><LearnCompanion size={120} pose={companionPose} mood={tone === "light" ? "writing" : "default"} speaking={speaking} /></div>}
        <motion.button
          type="button"
          onClick={onAction}
          disabled={!actionEnabled}
          aria-disabled={!actionEnabled}
          whileTap={actionEnabled ? { scale: 0.96, y: 3 } : undefined}
          animate={actionEnabled ? { scale: [1, 1.04, 1] } : { scale: 1 }}
          transition={actionEnabled ? { duration: 0.5, ease: "easeOut" } : undefined}
          style={{
            pointerEvents: "auto", minWidth: compact ? "60%" : 260, height: 58, borderRadius: 999, border: "none",
            fontFamily: LEARN.font, fontWeight: 700, fontSize: 19, color: "#fff",
            background: actionEnabled ? accent : "rgba(11,26,51,0.28)",
            boxShadow: actionEnabled ? LEARN.shadow.sticker(accentDeep) : "none",
            cursor: actionEnabled ? "pointer" : "not-allowed",
            transition: "background 200ms, box-shadow 200ms",
          }}
        >
          {actionLabel}
        </motion.button>
      </div>
    </div>
  );
}

function SpeakerIcon({ animate }) {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4 10v4h3l4 4V6L7 10H4z" fill="#fff" />
      <path d="M15 9.5a3.5 3.5 0 010 5" style={{ opacity: animate ? 1 : 0.9 }} />
      <path d="M17.5 7a7 7 0 010 10" style={{ opacity: animate ? 1 : 0.55 }} />
    </svg>
  );
}
